import { configGAM, configGTM, configSite } from "@pub/config";
import {
  addScriptSrc,
  Gam,
  GAMProps,
  loadScript,
} from "@sphtech/dmg-design-system";
import { DISABLE_ADS } from "@src/app/constants";
import { useEffect } from "react";

import gamSrc from "../../js/gam.js?url";
import { StoryData } from "../types/ServerCustomContext";
import headCssString from "./AdSettings.head.css?raw";
import { SDK_ADS_ENABLED } from "./sdkAds/config";
import SdkAdsShell from "./sdkAds/SdkAdsShell";

const sphmSiteName = configSite.name;
const sphmSiteAddress = configSite.name;
const sphmContainerId = configGTM.container;
const sphmAdAccount = configGAM.account;
const sphmAbm = !!configGAM.enableAbm;

type Props = Omit<GAMProps, "StoryData"> & {
  StoryData?: Partial<StoryData>;
};

export const NoAds = () => {
  useEffect(() => {
    document.body.classList.add("noads");
    return () => {
      document.body.classList.remove("noads");
    };
  }, []);
  return null;
};

// Publications where SDK_ADS_ENABLED is true render through the
// @sphtech/web2-ads SDK shell instead of the legacy SPHMGAM engine.
export default DISABLE_ADS ? NoAds : SDK_ADS_ENABLED ? SdkAdsShell : AdSettings;

// a self executing function injected into the head
const overlaySEF = `(function () {
  const htmlClassList = document.documentElement.classList;
  if (screen.width >= 1e3 && (!document.cookie.includes("prestitialDisplayed=yes") || window.location.search.includes("dfp_preview"))) {
    setTimeout(function () {
      if (!htmlClassList.contains("gotprestitial")) {
        htmlClassList.add("no-overlay");
      }
    }, 4e3);
  } else {
    htmlClassList.add("no-overlay");
  }
})()`;

function AdSettings({
  sectionName,
  pageType,
  pageTitle,
  adUnits,
  StoryData,
  currentLocation,
}: Props) {
  const { baseData } = Gam({
    sectionName,
    pageType,
    pageTitle,
    adUnits,
    categoryPrefix: configGAM.categoryPrefix || "",
    StoryData: {
      id: StoryData?.id || "",
      published: StoryData?.publishDate || "",
      updated: StoryData?.updateDate || "",
      authors: StoryData?.authors || [],
      tags: StoryData?.tags || [],
      typeName: StoryData?.typeName || "",
      urlPath: StoryData?.path || "",
      sections: StoryData?.sections || [],
      sponsorship: StoryData?.sponsorship || {},
    },
    currentLocation,
    siteConfig: {
      siteName: sphmSiteName,
      siteAddress: sphmSiteAddress,
      gtmContainer: sphmContainerId,
      adAccount: sphmAdAccount,
      adAbm: sphmAbm,
    },
  });

  useEffect(() => {
    const adCheckId = "sphm_ad_style";
    if (document.getElementById(adCheckId)) {
      return;
    }

    addScriptSrc(
      [
        "window.SPHMGAM=window.SPHMGAM||{cmd:[]};",
        `window.SPHMGAM.debounce=${configGAM.debounce ? "true" : "false"};`,
        `window.SPHMGAM.debounceDelay=${configGAM.debounceDelay || 200};`,
        "window.googletag=window.googletag||{cmd:[]};",
        "if(!googletag.slots){googletag.slots={};}",
        "window.loaded_dfp_tags=window.loaded_dfp_tags||'{}';",
        "window.gptadslots=window.gptadslots||[];",
        `window.sphm_base_data=${JSON.stringify(baseData)};`,
        overlaySEF,
      ].join(""),
      true,
    );

    void loadScript(
      "https://securepubads.g.doubleclick.net/tag/js/gpt.js",
      true,
    );

    void loadScript("https://adtag.sphdigital.com/tag/smx/prebid.js", true);

    void loadScript(gamSrc, true);

    if (window.gpt_grapeshots_promise) {
      window.gpt_grapeshots_promise
        .then(() => {
          window.SPHMGAM.cmd.push("start");
        })
        .catch(() => {
          window.SPHMGAM.cmd.push("start");
        });
    } else {
      window.SPHMGAM.cmd.push("start");
    }
  }, [baseData]);

  return (
    <>
      <style type="text/css">{headCssString}</style>
      <link
        href="https://adtag.sphdigital.com/tag/smx/smx_prebid.js"
        rel="prefetch"
      />
    </>
  );
}
